import authService from './authService';
import { Vehicle, VehicleFormData, VehicleSearchModel } from '../types/vehicle';
import { PaginatedResponse, ApiResponse } from '../types/common';

class VehicleService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  async getAll(searchModel: VehicleSearchModel): Promise<PaginatedResponse<Vehicle>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.is_active !== undefined && { is_active: searchModel.is_active.toString() }),
      ...(searchModel.for_route && { for_route: 'true' }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/vehicles?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar veículos');

    const json = await response.json();
    return {
      success: true,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: json.data?.pagination?.page ?? json.pagination?.page ?? 1,
        limit: json.data?.pagination?.page_size ?? json.pagination?.limit ?? searchModel.pageSize,
        total: json.data?.pagination?.total_items ?? json.pagination?.total ?? 0,
        totalPages: json.data?.pagination?.total_pages ?? json.pagination?.totalPages ?? 1,
      },
    };
  }

  async getById(id: number): Promise<Vehicle> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/vehicles/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Veículo não encontrado');
      throw new Error('Falha ao buscar veículo');
    }
    const data: ApiResponse<Vehicle> = await response.json();
    return data.data!;
  }

  async create(formData: VehicleFormData): Promise<Vehicle> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/vehicles', formData);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao criar veículo');
    }
    const data: ApiResponse<Vehicle> = await response.json();
    return data.data!;
  }

  async update(id: number, formData: Partial<VehicleFormData>): Promise<Vehicle> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/vehicles/${id}`, formData);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Veículo não encontrado');
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar veículo');
    }
    const data: ApiResponse<Vehicle> = await response.json();
    return data.data!;
  }

  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/vehicles/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Veículo não encontrado');
      throw new Error('Falha ao excluir veículo');
    }
  }

  /**
   * Lista todos os veículos ativos (sem paginação).
   * Usado para seleção de veículo na tela de emissão de nota fiscal.
   */
  async getActiveVehicles(): Promise<Vehicle[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/vehicles?is_active=true&limit=200');
    if (!response.ok) throw new Error('Falha ao buscar veículos');
    const json = await response.json();
    return Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []);
  }
}

export const vehicleService = new VehicleService();
export default vehicleService;
